import LZUTF8 from 'lzutf8';
import { DEV_SESSION_STORAGE_KEY, TOKEN_STORAGE_KEY } from '@constants/index';

/**
 * The id_token lifecycle for a standalone Palmetto app: the portal opens
 * `https://<site host>/<path>?id_token=<jwt>` (the API's REP_APP_URL, e.g. rep.cloudeoc.com);
 * the token is moved into
 * sessionStorage (never kept in the URL) and reused for the tab's lifetime.
 * Nothing here verifies the token - the portal account endpoint does that.
 */

const QUERY_PARAM = 'id_token';

/** Pulls `?id_token=` out of the current URL (and strips it) or reads the stored one. */
export const consumeToken = (): string | null => {
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get(QUERY_PARAM);
    if (fromUrl) {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, fromUrl);
      url.searchParams.delete(QUERY_PARAM);
      // Keep the deep link the portal (or a notification email) pointed at.
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
      return fromUrl;
    }
    return sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const storeToken = (token: string) => {
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // Storage may be unavailable (privacy mode); the in-memory token still works for this load.
  }
};

/**
 * Forgets the session: the app token and, in the dev build, the Cognito token
 * the LoginGate cached - otherwise "Return to sign in" after a wrong-account
 * sign-in would just re-use that cached token and show "Not authorized" again.
 */
export const clearToken = () => {
  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(DEV_SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** Decoded JWT payload, or null when the token is not a JWT (mock/offline tokens pass through). */
export const decodeJwt = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/** True when the token carries an `exp` that has already passed (with a minute of slack). */
export const isExpired = (token: string): boolean => {
  const payload = decodeJwt(token);
  const exp = payload?.exp;
  return typeof exp === 'number' && exp * 1000 < Date.now() + 60_000;
};

/**
 * The loopback access-token id carried inside the Cognito id_token: the
 * portal packs the account into `custom:chunk1..n` (base64 of lzutf8-compressed
 * JSON) and `accessToken` is one of its fields. Returns null when the token has
 * no chunks or they cannot be decoded.
 */
export const accessTokenIdFromToken = (token?: string | null): string | null => {
  if (!token) return null;
  const payload = decodeJwt(token);
  if (!payload) return null;
  // custom:chunk1..n, in order (the sibling apps' getAccountFromIdToken)
  const chunks = Object.keys(payload)
    .filter((k) => /^custom:chunk\d+$/.test(k))
    .sort((a, b) => Number(a.replace(/\D/g, '')) - Number(b.replace(/\D/g, '')))
    .map((k) => String(payload[k] ?? ''))
    .join('');
  if (!chunks) return null;
  try {
    const json = LZUTF8.decompress(chunks, { inputEncoding: 'Base64' }) as string;
    const account = JSON.parse(json) as { accessToken?: string };
    return account.accessToken || null;
  } catch {
    return null;
  }
};
